import React, {
  useState, useCallback, useEffect, useMemo, ReactElement, Fragment,
} from 'react';
import Loader from '../../../../components/loader/loader.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import TextBold from '../../../../components/text-bold/text-bold.component';
import ActionButtonNewItem from '../../../../components/action-button-new-item/action-button-new-item.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FieldMultiSelect from '../../../../components/field-multi-select/field-multi-select.component';
import FieldTextRich from '../../../../components/field-text-rich/field-text-rich.component';
import FieldUpload from '../../../../components/field-upload/field-upload.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import SendIcon from '../../../../icons/send-icon';
import CommentService from '../../../../services/comment-service';
import CompanyDefaultImage from '../../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const CommentForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isCommentSaving,
    isCommentFormActive,
    withLabels,
    withContactFields,
    commentText,
    commentLabel,
    attachmentFiles,
    selectedContactCompanyContacts,
    selectedContactPersonContacts,
    mentionRouteRoot,
    hashtagRouteRoot,
    labelIconMap,
    commentAttachmentDropDeps,
    mentionList,
    hashtagList,
    commentLabelList,
    contactCompanyList,
    contactPersonList,
    onSaveComment,
    onCommentFieldChange,
    onCommentFieldKeyDown,
    onCommentAttachmentFilesChange,
    onCommentAttachmentFileDelete,
    onCommentAttachmentFileError,
    onCommentLabelChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onCompanyContactsChange,
    onPersonContactsChange,
  } = props;

  const [isContactCompanyBlockVisible, onSetIsContactCompanyBlockVisible]: [boolean, Function] = useState(false);
  const [isContactPersonBlockVisible, onSetIsContactPersonBlockVisible]: [boolean, Function] = useState(false);

  const menuItemCommentLabelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemValueKey: 'name',
    optionList: commentLabelList,
    iconMap: labelIconMap || null,
  }), [commentLabelList, labelIconMap]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedContactCompanyContacts,
  }), [contactCompanyList, selectedContactCompanyContacts]);

  const menuItemContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedContactPersonContacts,
  }), [contactPersonList, selectedContactPersonContacts]);

  const companyContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onCompanyContactsChange) {
      onCompanyContactsChange(contactValues);
    }
  }, []);

  const personContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onPersonContactsChange) {
      onPersonContactsChange(contactValues);
    }
  }, []);

  const addPeopleButtonClick: Function = useCallback((): void => {
    onSetIsContactPersonBlockVisible(true);
  }, []);

  const addCompaniesButtonClick: Function = useCallback((): void => {
    onSetIsContactCompanyBlockVisible(true);
  }, []);

  useEffect((): void => {
    if (selectedContactCompanyContacts === null) {
      onSetIsContactCompanyBlockVisible(false);
    }
  }, [selectedContactCompanyContacts]);

  useEffect((): void => {
    if (selectedContactPersonContacts === null) {
      onSetIsContactPersonBlockVisible(false);
    }
  }, [selectedContactPersonContacts]);

  return (
    <PaddedFlexContainer isLarge isColumn isFlexStart>
      <PaddedFlexContainer isStart>
        <TextBold>
          Add Comment
        </TextBold>
      </PaddedFlexContainer>
      <FlexContainer isFullWidth>
        <FlexContainer isFullWidth>
          <FieldTextRich
            isPlain
            isSimple
            isFullWidth
            withMentions
            withHashtags
            withSendByEnter
            withAutoFocus={isCommentFormActive}
            value={commentText || ''}
            mentionList={mentionList}
            hashtagList={hashtagList}
            mentionRouteRoot={mentionRouteRoot}
            hashtagRouteRoot={hashtagRouteRoot}
            onFieldChange={onCommentFieldChange}
            onKeyDown={onCommentFieldKeyDown}
          />
        </FlexContainer>
        <FlexContainer>
          {
            !isCommentSaving && (
              <Tooltip title="Send Comment">
                <ActionButton
                  isDisabled={isCommentSaving}
                  id={CommentService.ID_COMMENT_SEND_BUTTON}
                  onClick={onSaveComment}
                >
                  <SendIcon />
                </ActionButton>
              </Tooltip>
            )
          }
          {
            isCommentSaving && (
              <Loader isInline isSmall />
            )
          }
        </FlexContainer>
      </FlexContainer>
      {
        withLabels && onCommentLabelChange && (
          <PaddedFlexContainer isExtended isFlexStart>
            <PaddedFlexContainer isStart>
              <TextBold>
                Label
              </TextBold>
            </PaddedFlexContainer>
            <FlexContainer>
              <FieldSelect
                isPlain
                isShort
                isBordered
                withIcons
                withBlankElement
                value={commentLabel}
                onChange={(event: EventSelectChange, value: string): void => {
                  onCommentLabelChange(value);
                }}
              >
                {menuItemCommentLabelList}
              </FieldSelect>
            </FlexContainer>
          </PaddedFlexContainer>
        )
      }
      <PaddedFlexContainer isExtended>
        <FieldUpload
          isPlain
          isDroppable
          isMultiple
          withDirectorySupport
          withMultipleSelection
          uploadFiles={attachmentFiles}
          dropDeps={commentAttachmentDropDeps}
          buttonLabel="Attach Files"
          onFilesChange={onCommentAttachmentFilesChange}
          onFileDelete={onCommentAttachmentFileDelete}
          onError={onCommentAttachmentFileError}
        />
      </PaddedFlexContainer>
      {
        withContactFields && (
          <Fragment>
            {
              isContactCompanyBlockVisible ? (
                <Fragment>
                  <PaddedFlexContainer isStart>
                    <TextBold>
                      Companies
                    </TextBold>
                  </PaddedFlexContainer>
                  <FlexContainer isFullWidth>
                    <FieldMultiSelect
                      isPlain
                      isBordered
                      withImage
                      withSearch
                      isDisabled={contactCompanyList && !contactCompanyList.length}
                      nameKey="fullName"
                      onSearch={onContactCompanySearch}
                      onClose={onContactCompanySearchClose}
                      optionList={contactCompanyList}
                      onValuesChange={companyContactsChange}
                      values={selectedContactCompanyContacts}
                      defaultImage={CompanyDefaultImage}
                    >
                      {menuItemContactCompanyList}
                    </FieldMultiSelect>
                  </FlexContainer>
                </Fragment>
              ) : (
                <PaddedFlexContainer>
                  <ActionButtonNewItem
                    withNoHighlight
                    label="Add Companies"
                    onClick={addCompaniesButtonClick}
                  />
                </PaddedFlexContainer>
              )
            }
          </Fragment>
        )
      }
      {
        withContactFields && (
          <Fragment>
            {
              isContactPersonBlockVisible ? (
                <Fragment>
                  <PaddedFlexContainer isStart>
                    <TextBold>
                      People
                    </TextBold>
                  </PaddedFlexContainer>
                  <FlexContainer isFullWidth>
                    <FieldMultiSelect
                      isPlain
                      isBordered
                      isFullWidth
                      withImage
                      withSearch
                      isDisabled={contactPersonList && !contactPersonList.length}
                      nameKey="fullName"
                      onSearch={onContactPersonSearch}
                      onClose={onContactPersonSearchClose}
                      optionList={contactPersonList}
                      onValuesChange={personContactsChange}
                      values={selectedContactPersonContacts}
                      defaultImage={PersonDefaultImage}
                    >
                      {menuItemContactPersonList}
                    </FieldMultiSelect>
                  </FlexContainer>
                </Fragment>
              ) : (
                <PaddedFlexContainer>
                  <ActionButtonNewItem
                    withNoHighlight
                    label="Add People"
                    onClick={addPeopleButtonClick}
                  />
                </PaddedFlexContainer>
              )
            }
          </Fragment>
        )
      }
    </PaddedFlexContainer>
  );
});

export default CommentForm;
